import type { PrescriptionT } from '@lotushealth/lotushealth_js_sdk/schemas';

import { buildRenewalBadge, medicationStatusLabel, prescriptionFieldLabel } from '@/entities/rx';
import { toTableCellText } from '@/shared/lib/table';
import { LabeledFieldGrid } from '@/shared/ui/primitives';
import { Badge } from '@/shared/ui/shadcn/badge';
import { statusToneClassName } from '@/shared/ui/statusTone';

import { REFILL_PRESCRIPTION_FIELDS } from './prescriptionDetailFields';

interface SectionProps {
  readonly title: string;
  readonly prescription: PrescriptionT | null | undefined;
  readonly emptyHint: string;
}

function PrescriptionSection({ title, prescription, emptyHint }: SectionProps) {
  if (prescription === null || prescription === undefined) {
    return (
      <section
        aria-label={title}
        data-testid={`refill-section-${title.toLowerCase()}`}
        className="rounded-md border p-3"
      >
        <header className="mb-2 flex items-center justify-between gap-2">
          <h4 className="text-sm font-semibold">{title}</h4>
        </header>
        <p className="text-muted-foreground text-xs">{emptyHint}</p>
      </section>
    );
  }

  const status = medicationStatusLabel(prescription.MedicationStatus);
  const renewal = buildRenewalBadge(prescription);
  const displayName = toTableCellText(prescription.DisplayName);

  return (
    <section
      aria-label={title}
      data-testid={`refill-section-${title.toLowerCase()}`}
      className="rounded-md border p-3"
    >
      <header className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <div className="flex min-w-0 flex-col">
          <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            {title}
          </span>
          <span className="truncate text-sm font-medium">{displayName}</span>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {renewal.show ? (
            <Badge className={statusToneClassName('info')} aria-label="Renewal request">
              {renewal.label}
            </Badge>
          ) : null}
          <Badge className={statusToneClassName(status.tone)}>{status.label}</Badge>
        </div>
      </header>

      <LabeledFieldGrid
        items={REFILL_PRESCRIPTION_FIELDS.map((key) => ({
          label: prescriptionFieldLabel(key),
          value: toTableCellText(prescription[key]),
        }))}
      />

      {renewal.note !== null ? (
        <p className="text-muted-foreground mt-2 text-xs italic">Renewal note: {renewal.note}</p>
      ) : null}
    </section>
  );
}

export interface RefillPrescriptionSectionsProps {
  readonly requested: PrescriptionT | null | undefined;
  readonly original: PrescriptionT | null | undefined;
  readonly dispensed: PrescriptionT | null | undefined;
}

/** Requested / Original / Dispensed — Dispensed surfaces partial-fill context. */
export function RefillPrescriptionSections({
  requested,
  original,
  dispensed,
}: RefillPrescriptionSectionsProps) {
  return (
    <div className="flex flex-col gap-3">
      <PrescriptionSection
        title="Requested"
        prescription={requested}
        emptyHint="No requested prescription on this refill."
      />
      <PrescriptionSection
        title="Original"
        prescription={original}
        emptyHint="DoseSpot did not return the original prescription."
      />
      <PrescriptionSection
        title="Dispensed"
        prescription={dispensed}
        emptyHint="No dispensed prescription yet — partial-fill context will appear here."
      />
    </div>
  );
}
